import { Stethoscope, Drill, PawPrint, HeartPulse, Baby, Dna, Boxes, Snowflake, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { IMG } from "@/lib/images";

const FEATURED = [
  {
    id: "hospitals",
    title: "Hospitals & Health Systems",
    img: "/assets/hero-bright.png",
    desc: "Customized courier service models that help hospitals and health systems operate efficiently — couriers trained in HIPAA awareness and Bloodborne Pathogen safety, equipped with barcode scanners, GPS, specimen coolers, spill kits, and ID badges.",
  },
  {
    id: "pharmacies",
    title: "Pharmacies",
    img: "/assets/pharmacy-ai.png",
    desc: "Specialists in pharmacy home delivery. On-demand and scheduled route services that prioritize customer safety while helping pharmacies provide fast, reliable same-day delivery.",
  },
  {
    id: "medical-labs",
    title: "Medical Labs",
    img: IMG.labOptics,
    desc: "Same-day lab courier service across the DMV on a regular basis — adaptable technology and experienced professionals ensure samples arrive within hours of order confirmation.",
  },
];

const TILES = [
  { icon: Stethoscope, title: "Healthcare Providers & Clinics", desc: "Records, supplies, and specimens moved securely between clinics and care sites." },
  { icon: Drill, title: "Dental Laboratories", desc: "Crowns, implants, and prosthetic cases shuttled on dependable daily routes." },
  { icon: PawPrint, title: "Veterinary Clinics", desc: "Documents, supplies, and lab samples delivered same-day to keep pets healthy." },
  { icon: HeartPulse, title: "Infusion Centers", desc: "Specimens and medications transported with care — routine and time-sensitive." },
  { icon: Baby, title: "Fertility Clinics", desc: "Secure, time-sensitive cryo-shipping coordination for IVF and reproductive laboratories." },
  { icon: Dna, title: "Clinical Trials", desc: "Investigational medications, biological samples, and devices handled to regulatory standards." },
  { icon: Boxes, title: "Medical Supply Companies", desc: "Time-critical devices and equipment delivered when every second counts." },
  { icon: Snowflake, title: "Cryogenics", desc: "Through careful coordination, secure handling, temperature control, and dependable delivery, we help protect the integrity of reproductive specimens, stem cells, research samples, and other critical medical materials." },
];

export const Facilities = () => (
  <section id="facilities" data-testid="facilities-section" className="bg-white py-24 lg:py-32 scroll-mt-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Overline>Facilities We Serve — Chapter 03</Overline>
        <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
          One fleet. Every kind of care facility.
        </h2>
      </Reveal>

      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {FEATURED.map((f, i) => (
          <Reveal key={f.id} delay={i * 0.08}>
            <div data-testid={`facility-card-${f.id}`}
                 className="group rounded-[1.5rem] overflow-hidden border border-slate-200/80 bg-white shadow-sm hover:shadow-xl hover:border-blue-500/40 transition-all duration-300">
              <div className="relative h-52 overflow-hidden">
                <img src={f.img} alt={f.title} data-testid={`facility-image-${f.id}`}
                     className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D4F]/55 to-transparent" />
                <h3 className="absolute bottom-4 left-5 font-display text-xl font-bold text-white tracking-tight">{f.title}</h3>
              </div>
              <div className="p-6">
                <p className="text-sm leading-relaxed text-slate-600">{f.desc}</p>
                <a href="#quote" data-testid={`facility-learn-more-${f.id}`}
                   className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#0047FF] hover:gap-2.5 transition-all duration-200">
                  Learn more <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {TILES.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.05} className={i === TILES.length - 1 ? "sm:col-span-2 lg:col-span-1" : ""}>
            <div data-testid={`facility-tile-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                 className="group h-full rounded-2xl border border-slate-200/80 bg-[#F8FAFC] p-6 hover:bg-white hover:shadow-lg hover:border-blue-500/40 transition-all duration-300">
              <span className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center group-hover:bg-[#0047FF] transition-colors duration-300">
                <t.icon className="h-5 w-5 text-[#0047FF] group-hover:text-white transition-colors duration-300" />
              </span>
              <h3 className="mt-4 font-display text-base font-bold text-slate-900 tracking-tight">{t.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
